import React from 'react';
import { Box } from '@mui/material';
import { LocalShipping as ShippingIcon } from '@mui/icons-material';

// The three standing promises, in the prototype's order. The first repeats at the end so the roll
// returns to the top without a visible jump back.
export const PROMISES = [
  'Free delivery on every order — always, no minimum',
  'Printed on real card stock and posted for you',
  'Arrives at their door within five days',
];

const LINE_H = 18; // px — one promise per line; the roll steps by exactly this

/**
 * The delivery-promise strip that sits directly under the store header on every page
 * (design: CardStoreApp.dc.html vRevised, the band between <header> and the route body).
 *
 * It replaces the "Free Shipping" tab that used to hang off the cart icon: the same reassurance,
 * said in full, above the fold, on every route rather than only where the cart is looked at.
 *
 * The roll is a pure CSS translate over a stacked column — no timers, no state, nothing to leak —
 * and collapses to the first line under prefers-reduced-motion.
 */
const PromiseStrip = () => (
  <Box
    data-testid="promise-strip"
    sx={{
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: '#eef6ea',
      borderBottom: '1px solid #dcebd2',
      px: { xs: 1.5, md: 3 },
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      height: 34,
      '@keyframes tickerRoll': {
        '0%, 22%': { transform: 'translateY(0)' },
        '27%, 50%': { transform: `translateY(-${LINE_H}px)` },
        '55%, 78%': { transform: `translateY(-${LINE_H * 2}px)` },
        '83%, 100%': { transform: `translateY(-${LINE_H * 3}px)` },
      },
      '@keyframes stampTick': {
        '0%, 88%': { transform: 'rotate(-4deg)' },
        '94%': { transform: 'rotate(4deg)' },
        '100%': { transform: 'rotate(-4deg)' },
      },
    }}
  >
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: 20,
        height: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#4d7a2c',
        color: '#fff',
        border: '1.5px dashed rgba(255,255,255,0.85)',
        borderRadius: '2px',
        transform: 'rotate(-4deg)',
        animation: 'stampTick 9s ease-in-out infinite',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <ShippingIcon sx={{ fontSize: 12 }} />
    </Box>
    <Box sx={{ position: 'relative', height: LINE_H, overflow: 'hidden', flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          animation: 'tickerRoll 13.5s cubic-bezier(0.7,0,0.2,1) infinite',
          // Reduced motion gets the first promise, held — the message still lands, nothing moves.
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {[...PROMISES, PROMISES[0]].map((promise, i) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={`${promise}-${i}`}
            component="span"
            // The trailing repeat exists only so the roll returns to the top without a jump — it is
            // the same sentence, so hide it from assistive tech rather than say it twice.
            aria-hidden={i === PROMISES.length ? 'true' : undefined}
            sx={{
              height: LINE_H,
              lineHeight: `${LINE_H}px`,
              fontSize: '0.72rem',
              fontWeight: 600,
              color: '#3f5a2c',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {promise}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export default PromiseStrip;
